"use client";
import { Dialog } from "@headlessui/react";
import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "../../utils/toast";
import {
  permanantdeletefunction,
  softdeletefunction,
} from "../../service/Task";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";

export const DeleteOption = ({ fetchData, deleteOption, setDeleteOption, fileid }) => {
  const [deletionType, setDeletionType] = useState(null);
  const [step, setStep] = useState("select");
  const [isDeleting, setIsDeleting] = useState(false);
  const stepRef = useRef(step);
  const { langMode } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const closeDeleteOption = () => {
    setStep("select");
    setDeleteOption(false);
    setDeletionType(null);
    setIsDeleting(false);
  };

  const handleDialogClose = () => {
    if (stepRef.current === "confirm") {
      setStep("select");
      return;
    }
    closeDeleteOption();
  };

  const handleBackToSelect = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setStep("select");
  };

  const handleApply = () => {
    if (deletionType) {
      setStep("confirm");
    } else {
      toast.error(translations[langMode].plzselectdeltype);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      if (deletionType === "soft") {
        await softdeletefunction(fileid, langMode);
      } else if (deletionType === "permanent") {
        await permanantdeletefunction(fileid, langMode);
      }
      fetchData();
      closeDeleteOption();
    } catch (error) {
      toast.error(translations[langMode].failedtodelfile);
      setIsDeleting(false);
    }
  };

  const confirmDeleteText =
    deletionType === "soft"
      ? translations[langMode].dosoftdeletions
      : deletionType === "permanent"
        ? translations[langMode].depermanatdeletions
        : translations[langMode].confirmdeletion;

  return (
    <Dialog open={deleteOption} onClose={handleDialogClose} className="relative z-50">
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[1px]" aria-hidden="true" />
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel
            className="relative transform overflow-hidden rounded-2xl border border-slate-100 bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
            onClick={(event) => event.stopPropagation()}
          >
            {step === "select" ? (
              <>
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div
                      className={`mt-3 text-center sm:mt-0 w-full ${
                        isRtl ? "sm:text-right" : "sm:ml-4 sm:text-left"
                      }`}
                    >
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-slate-800 mt-2"
                      >
                        {translations[langMode].deletetask}
                      </Dialog.Title>
                      <div className="mt-4 flex flex-col gap-2">
                        <label
                          className={`flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${
                            isRtl ? "flex-row-reverse justify-end text-right" : ""
                          } ${deletionType === "soft" ? "border-[#236DB4] bg-blue-50/60" : ""}`}
                        >
                          <input
                            type="radio"
                            className="mt-0.5 accent-[#236DB4]"
                            checked={deletionType === "soft"}
                            onChange={() => setDeletionType("soft")}
                          />
                          <span className="text-sm font-medium text-slate-700">{translations[langMode].dosoftdeletions}</span>
                        </label>
                        <label
                          className={`flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2.5 cursor-not-allowed opacity-50 ${
                            isRtl ? "flex-row-reverse justify-end text-right" : ""
                          }`}
                          title={translations[langMode].permanentdeletedisabled}
                        >
                          <input
                            type="radio"
                            className="mt-0.5 cursor-not-allowed"
                            disabled
                            checked={deletionType === "permanent"}
                            onChange={() => setDeletionType("permanent")}
                          />
                          <span className="text-sm font-medium text-slate-700 cursor-not-allowed">{translations[langMode].depermanatdeletions}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`bg-slate-50 px-4 py-3 sm:flex sm:px-6 sm:gap-2 ${
                    isRtl ? "sm:flex-row" : "sm:flex-row-reverse"
                  }`}
                >
                  <button data-button-intent="primary"
                    type="button"
                    onClick={handleApply}
                    className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 sm:w-auto"
                  >
                    {translations[langMode].apply}
                  </button>
                  <button
                    type="button"
                    onClick={closeDeleteOption}
                    className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50 sm:mt-0 sm:w-auto"
                  >
                    {translations[langMode].cancle}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white px-4 py-5 sm:p-6">
                  <div className={`text-center ${isRtl ? "sm:text-right" : "sm:text-left"}`}>
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-slate-800"
                    >
                      {translations[langMode].confirmdeletion}
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-slate-500">{confirmDeleteText}</p>
                  </div>
                </div>
                <div
                  className={`bg-slate-50 px-4 py-3 sm:flex sm:px-6 sm:gap-2 ${
                    isRtl ? "sm:flex-row" : "sm:flex-row-reverse"
                  }`}
                >
                  <button data-button-intent="danger"
                    type="button"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                  >
                    {isDeleting ? translations[langMode].Processing : translations[langMode].Confirm}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToSelect}
                    disabled={isDeleting}
                    className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50 disabled:opacity-50 sm:mt-0 sm:w-auto"
                  >
                    {translations[langMode].cancle}
                  </button>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};
